import { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import TailgateForm from "@/components/TailgateForm";
import { toast } from "sonner";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Users,
  FileWarning,
} from "lucide-react";

export default function TailgatesTab({ project, onChanged }) {
  const [mode, setMode] = useState("list"); // "list" | "new" | "edit"
  const [editing, setEditing] = useState(null);

  const tailgates = project.tailgates || [];

  const startNew = () => {
    setEditing(null);
    setMode("new");
  };
  const openEdit = (tg) => {
    setEditing(tg);
    setMode("edit");
  };
  const backToList = () => {
    setEditing(null);
    setMode("list");
  };

  const remove = async (tg) => {
    if (!window.confirm(`Delete the tailgate from ${tg.date}? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${project.id}/tailgate/${tg.id}`);
      toast.success("Tailgate deleted");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (mode === "new" || mode === "edit") {
    return (
      <div className="space-y-3" data-testid="tailgate-edit-view">
        <button
          onClick={backToList}
          className="text-sm text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"
          data-testid="tailgate-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back to tailgate list
        </button>
        <TailgateForm
          project={project}
          initial={editing}
          mode={mode}
          onSaved={() => { backToList(); onChanged?.(); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tailgate-list-view">
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-5 flex items-center justify-between gap-3">
          <div>
            <div className="font-display font-bold text-lg">Daily Tailgate Meetings</div>
            <div className="text-xs text-slate-400 mt-0.5">
              One per day on site. Each entry captures the day&rsquo;s scope of work, crew sign-in, hazards and PPE.
            </div>
          </div>
          <Button
            onClick={startNew}
            className="bg-red-700 hover:bg-red-600 font-display tracking-wider shrink-0"
            data-testid="add-daily-tailgate"
          >
            <Plus className="w-4 h-4 mr-1.5" /> ADD DAILY TAILGATE
          </Button>
        </CardContent>
      </Card>

      {tailgates.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-8 text-center text-slate-400" data-testid="tailgate-empty">
            <FileWarning className="w-7 h-7 mx-auto mb-3 text-amber-500" />
            No tailgate meetings yet for this project.
            <div className="mt-2 text-xs">Tap <b>ADD DAILY TAILGATE</b> above to record the first one.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {tailgates.map((tg) => (
            <Card
              key={tg.id}
              className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors"
              data-testid={`tailgate-card-${tg.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 font-mono text-blue-400 text-sm">
                    <CalendarDays className="w-4 h-4" />
                    {tg.date || "(no date)"}
                  </div>
                  <button
                    onClick={() => remove(tg)}
                    className="text-slate-500 hover:text-red-400 p-1"
                    aria-label="Delete tailgate"
                    data-testid={`tailgate-delete-${tg.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-2 font-display font-bold truncate">{tg.site || project.site_name}</div>
                <div className="mt-1 text-sm text-slate-300 line-clamp-2 min-h-[40px]">
                  {tg.work_scope ? tg.work_scope : <span className="text-slate-500 italic">No scope recorded for the day.</span>}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                  <div className="inline-flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {(tg.signatures || []).length} signatures
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(tg)}
                    className="text-blue-400 hover:text-blue-300 hover:bg-slate-800 h-7"
                    data-testid={`tailgate-open-${tg.id}`}
                  >
                    Open <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
